import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { Shield, Smartphone, Copy, Check, Loader2, Download, Key, AlertTriangle } from "lucide-react";

interface MFAEnrollmentProps {
  onEnrollmentComplete: () => void;
}

export function MFAEnrollment({ onEnrollmentComplete }: MFAEnrollmentProps) {
  const [enrolling, setEnrolling] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [factorId, setFactorId] = useState<string | null>(null);
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [otpCode, setOtpCode] = useState("");
  const [copiedSecret, setCopiedSecret] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Recovery codes state
  const [showRecoveryCodes, setShowRecoveryCodes] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [loadingCodes, setLoadingCodes] = useState(false);
  const [copiedCodes, setCopiedCodes] = useState(false);
  const [savedConfirmed, setSavedConfirmed] = useState(false);

  const startEnrollment = async () => {
    setEnrolling(true);
    setError(null);

    try {
      // Clean up any stale unverified factors first
      const { data: factors } = await supabase.auth.mfa.listFactors();
      if (factors?.totp) {
        for (const factor of factors.totp) {
          if ((factor.status as string) === 'unverified') {
            await supabase.auth.mfa.unenroll({ factorId: factor.id });
          }
        }
      }

      const { data, error } = await supabase.auth.mfa.enroll({
        factorType: 'totp',
        friendlyName: 'Authenticator App'
      });

      if (error) throw error;

      setFactorId(data.id);
      setQrCode(data.totp.qr_code);
      setSecret(data.totp.secret);
    } catch (err: any) {
      console.error('MFA enrollment error:', err);
      setError(err.message || 'Failed to start MFA enrollment');
      toast({
        title: "Error",
        description: err.message || 'Failed to start MFA enrollment',
        variant: "destructive",
      });
    } finally {
      setEnrolling(false);
    }
  };

  const verifyAndComplete = async () => {
    if (!factorId || otpCode.length !== 6) return;

    setVerifying(true);
    setError(null);

    try {
      // First challenge
      const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({
        factorId,
      });

      if (challengeError) throw challengeError;

      // Then verify
      const { error: verifyError } = await supabase.auth.mfa.verify({
        factorId,
        challengeId: challengeData.id,
        code: otpCode,
      });

      if (verifyError) throw verifyError;

      // Generate recovery codes
      setLoadingCodes(true);
      const { data: codesData, error: codesError } = await supabase.functions.invoke('mfa-recovery-codes', {
        body: { action: 'generate' }
      });

      if (codesError) throw codesError;

      setRecoveryCodes(codesData.codes);
      setShowRecoveryCodes(true);
      
    } catch (err: any) {
      console.error('MFA verification error:', err);
      setError(err.message || 'Verification failed. Please check the code and try again.');
      setOtpCode("");
    } finally {
      setVerifying(false);
      setLoadingCodes(false);
    }
  };

  const copySecret = async () => {
    if (secret) {
      await navigator.clipboard.writeText(secret);
      setCopiedSecret(true);
      setTimeout(() => setCopiedSecret(false), 2000);
    }
  };

  const copyAllCodes = async () => {
    const codesText = recoveryCodes.join('\n');
    await navigator.clipboard.writeText(codesText);
    setCopiedCodes(true);
    setTimeout(() => setCopiedCodes(false), 2000);
    toast({
      title: "Copied",
      description: "Recovery codes copied to clipboard",
    });
  };

  const downloadCodes = () => {
    const codesText = `PTA Hub MFA Recovery Codes\n${'='.repeat(30)}\n\nSave these codes in a safe place.\nEach code can only be used once.\n\n${recoveryCodes.join('\n')}\n\nGenerated: ${new Date().toLocaleDateString()}`;
    const blob = new Blob([codesText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'pta-hub-recovery-codes.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast({
      title: "Downloaded",
      description: "Recovery codes saved to file",
    });
  };

  const finishEnrollment = () => {
    toast({
      title: "MFA Enabled",
      description: "Two-factor authentication has been successfully enabled for your account.",
    });
    onEnrollmentComplete();
  };

  const cancelEnrollment = async () => {
    if (factorId) {
      try {
        await supabase.auth.mfa.unenroll({ factorId });
      } catch (err) {
        console.error('Failed to cancel enrollment:', err);
      }
    }
    setFactorId(null);
    setQrCode(null);
    setSecret(null);
    setOtpCode("");
    setError(null);
    setShowRecoveryCodes(false);
    setRecoveryCodes([]);
    setSavedConfirmed(false);
  };

  // Show recovery codes after verification
  if (showRecoveryCodes && recoveryCodes.length > 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Key className="h-5 w-5 text-amber-500" />
            Save Your Recovery Codes
          </CardTitle>
          <CardDescription>
            If you lose access to your authenticator app, you can use one of these codes to sign in. Each code can only be used once.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <Alert className="border-amber-500/50 bg-amber-50 dark:bg-amber-950/20">
            <AlertTriangle className="h-4 w-4 text-amber-600" />
            <AlertDescription className="text-amber-800 dark:text-amber-200">
              <strong>Important:</strong> Save these codes now. You won't be able to see them again!
            </AlertDescription>
          </Alert>

          <div className="grid grid-cols-2 gap-2 p-4 bg-muted/50 rounded-lg">
            {recoveryCodes.map((code, index) => (
              <code 
                key={index} 
                className="px-3 py-2 bg-background rounded text-sm font-mono text-center border"
              >
                {code}
              </code>
            ))}
          </div>

          <div className="flex gap-2">
            <Button variant="outline" onClick={copyAllCodes} className="flex-1">
              {copiedCodes ? (
                <>
                  <Check className="mr-2 h-4 w-4 text-green-500" />
                  Copied!
                </>
              ) : (
                <>
                  <Copy className="mr-2 h-4 w-4" />
                  Copy All
                </>
              )}
            </Button>
            <Button variant="outline" onClick={downloadCodes} className="flex-1">
              <Download className="mr-2 h-4 w-4" />
              Download
            </Button>
          </div>

          <div className="flex items-center space-x-2 pt-4 border-t">
            <Checkbox 
              id="confirm-saved" 
              checked={savedConfirmed}
              onCheckedChange={(checked) => setSavedConfirmed(checked === true)}
            />
            <label
              htmlFor="confirm-saved"
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              I have saved my recovery codes in a safe place
            </label>
          </div>

          <Button 
            onClick={finishEnrollment}
            disabled={!savedConfirmed}
            className="w-full"
          >
            Continue
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Initial state - not enrolling
  if (!qrCode) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Two-Factor Authentication
          </CardTitle>
          <CardDescription>
            Add an extra layer of security to your account by requiring a verification code from your authenticator app.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-muted-foreground">
              Not Enabled
            </Badge>
          </div>
          
          <div className="space-y-3 text-sm text-muted-foreground">
            <p className="font-medium text-foreground">Before you begin, you'll need:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>An authenticator app like Google Authenticator, Authy, or 1Password</li>
              <li>Your mobile device ready to scan a QR code</li>
            </ul>
          </div>

          <Button onClick={startEnrollment} disabled={enrolling}>
            {enrolling ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Starting...
              </>
            ) : (
              <>
                <Smartphone className="mr-2 h-4 w-4" />
                Enable Two-Factor Authentication
              </>
            )}
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Enrollment in progress - show QR code
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Shield className="h-5 w-5" />
          Set Up Two-Factor Authentication
        </CardTitle>
        <CardDescription>
          Scan the QR code with your authenticator app, then enter the verification code.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="space-y-4">
          <div className="flex flex-col items-center gap-4">
            <div className="p-4 bg-white rounded-lg">
              <img 
                src={qrCode} 
                alt="MFA QR Code" 
                className="w-48 h-48"
              />
            </div>
            <p className="text-sm text-muted-foreground text-center">
              Scan this QR code with your authenticator app
            </p>
          </div>

          <div className="space-y-2">
            <p className="text-sm font-medium">Can't scan? Enter this code manually:</p>
            <div className="flex items-center gap-2">
              <code className="flex-1 px-3 py-2 bg-muted rounded text-sm font-mono break-all">
                {secret}
              </code>
              <Button 
                variant="outline" 
                size="icon"
                onClick={copySecret}
              >
                {copiedSecret ? (
                  <Check className="h-4 w-4 text-green-500" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>
        </div>

        <div className="space-y-3">
          <Label className="text-sm font-medium">Enter the 6-digit code from your app:</Label>
          <div className="flex justify-center">
            <InputOTP
              maxLength={6}
              value={otpCode}
              onChange={setOtpCode}
            >
              <InputOTPGroup>
                <InputOTPSlot index={0} />
                <InputOTPSlot index={1} />
                <InputOTPSlot index={2} />
                <InputOTPSlot index={3} />
                <InputOTPSlot index={4} />
                <InputOTPSlot index={5} />
              </InputOTPGroup>
            </InputOTP>
          </div>
        </div>

        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={cancelEnrollment}
            className="flex-1"
          >
            Cancel
          </Button>
          <Button 
            onClick={verifyAndComplete}
            disabled={otpCode.length !== 6 || verifying || loadingCodes}
            className="flex-1"
          >
            {verifying || loadingCodes ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {loadingCodes ? 'Generating codes...' : 'Verifying...'}
              </>
            ) : (
              "Verify & Enable"
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
